import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { motion, AnimatePresence } from "framer-motion";
import { trpc } from "@/trpc/client";
import { useAuthStore } from "@/store/authStore";
import { toast } from "sonner";
import {
  VerificationFormSchema,
  TVerificationFormSchema,
} from "@/lib/validators/onboarding";
import { Gender } from "@prisma/client";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Loader2,
  User,
  FileImage,
  UserCheck,
  ArrowRight,
  CheckCircle,
  HelpCircle,
  Upload,
  Camera,
} from "lucide-react";
import { FaceMeshWebcam } from "@/components/FaceMeshWebcam";
import { AutoCaptureIDWebcam } from "@/components/AutoCaptureIDWebcam";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";

const StepIndicator = ({ currentStep }: { currentStep: number }) => {
  const steps = ["Details", "National ID", "Live Photo"];
  return (
    <div className="flex items-center justify-center mb-8">
      {steps.map((label, idx) => {
        const stepNum = idx + 1;
        const isActive = currentStep === stepNum;
        const isCompleted = currentStep > stepNum;
        return (
          <React.Fragment key={label}>
            <div className="flex flex-col items-center">
              <div
                className={`w-10 h-10 rounded-full border-2 flex items-center justify-center font-medium ${
                  isCompleted
                    ? "bg-primary border-primary text-primary-foreground"
                    : isActive
                    ? "bg-primary/20 border-primary text-primary"
                    : "border-border text-muted-foreground"
                }`}
              >
                {isCompleted ? <CheckCircle size={20} /> : stepNum}
              </div>
              <span className="mt-2 text-xs">{label}</span>
            </div>
            {stepNum < steps.length && (
              <div
                className={`flex-1 h-1 mx-2 rounded-full ${
                  currentStep > stepNum ? "bg-primary" : "bg-border"
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const VerificationPage = () => {
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const [step, setStep] = useState(1);
  const [isUploading, setIsUploading] = useState(false);
  const [capturedIdImage, setCapturedIdImage] = useState<string | null>(
    null
  );
  const [capturedFaceImage, setCapturedFaceImage] = useState<string | null>(
    null
  );
  const [idCaptureMethod, setIdCaptureMethod] = useState<'camera' | 'upload'>('camera');
  const [faceCaptureMethod, setFaceCaptureMethod] = useState<'camera' | 'upload'>('camera');

  const form = useForm<TVerificationFormSchema>({
    resolver: zodResolver(VerificationFormSchema),
    mode: "onChange",
    defaultValues: { currentResidence: "", organizationName: "" },
  });

  // Load saved progress from localStorage on mount
  useEffect(() => {
    const savedProgress = localStorage.getItem('onboarding_progress');
    if (savedProgress) {
      try {
        const { step: savedStep, formData } = JSON.parse(savedProgress);
        if (savedStep) setStep(savedStep);
        if (formData) {
          if (formData.gender) form.setValue('gender', formData.gender);
          if (formData.currentResidence) form.setValue('currentResidence', formData.currentResidence);
          if (formData.organizationName) form.setValue('organizationName', formData.organizationName);
        }
      } catch (e) {
        console.error('Failed to load saved progress:', e);
      }
    }
  }, [form]);

  // Save progress to localStorage whenever form values change
  useEffect(() => {
    const subscription = form.watch((formData) => {
      localStorage.setItem('onboarding_progress', JSON.stringify({
        step,
        formData: {
          gender: formData.gender,
          currentResidence: formData.currentResidence,
          organizationName: formData.organizationName,
        }
      }));
    });
    return () => subscription.unsubscribe();
  }, [form, step]);

  const completeVerificationMutation =
    trpc.user.completeVerification.useMutation({
      onSuccess: (updatedUser) => {
        // Update auth store with new user data
        const { setUser, user: currentUser } = useAuthStore.getState();
        if (currentUser) {
          setUser({
            ...currentUser,
            onboardingStatus: updatedUser.onboardingStatus,
          });
        }
        
        toast.success("Verification submitted!");
        localStorage.removeItem('onboarding_progress'); // Clear saved progress
        navigate("/onboarding/pending", { replace: true });
      },
      onError: (err) => toast.error(err.message),
    });

  const onIdCapture = useCallback(
    (file: File) => {
      setCapturedIdImage(URL.createObjectURL(file));
      form.setValue("nationalIdFile", [file], { shouldValidate: true });
    },
    [form]
  );
  const onIdRecapture = useCallback(() => {
    if (capturedIdImage) URL.revokeObjectURL(capturedIdImage);
    setCapturedIdImage(null);
    form.setValue("nationalIdFile", undefined, { shouldValidate: true });
  }, [capturedIdImage, form]);

  const onFaceCapture = useCallback(
    (file: File) => {
      setCapturedFaceImage(URL.createObjectURL(file));
      form.setValue("profilePictureFile", [file], { shouldValidate: true });
    },
    [form]
  );
  const onFaceRecapture = useCallback(() => {
    if (capturedFaceImage) URL.revokeObjectURL(capturedFaceImage);
    setCapturedFaceImage(null);
    form.setValue("profilePictureFile", undefined, {
      shouldValidate: true,
    });
  }, [capturedFaceImage, form]);

  const onIdFileUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        setCapturedIdImage(URL.createObjectURL(file));
        form.setValue("nationalIdFile", [file], { shouldValidate: true });
      }
    },
    [form]
  );

  const onFaceFileUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        setCapturedFaceImage(URL.createObjectURL(file));
        form.setValue("profilePictureFile", [file], { shouldValidate: true });
      }
    },
    [form]
  );

  const onSubmit = async (data: TVerificationFormSchema) => {
    setIsUploading(true);
    try {
      // —— ID upload ——  
      const idForm = new FormData();
      idForm.append("files", data.nationalIdFile[0]);        // ← changed from "images"
      idForm.append("type", "national_ids");

      const idRes = await fetch(`${apiUrl}/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: idForm,
      });
      const idJson = await idRes.json();
      if (!idRes.ok) throw new Error(idJson.error);

      // —— Face upload ——  
      const faceForm = new FormData();
      faceForm.append("files", data.profilePictureFile[0]);  // ← changed from "images"
      faceForm.append("type", "profile_pics");

      const faceRes = await fetch(`${apiUrl}/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: faceForm,
      });
      const faceJson = await faceRes.json();
      if (!faceRes.ok) throw new Error(faceJson.error);

      // —— Complete verification ——
      completeVerificationMutation.mutate({
        gender: data.gender as Gender,
        currentResidence: data.currentResidence,
        organizationName: data.organizationName,
        nationalIdPath: idJson.files[0].filePath,
        profilePicturePath: faceJson.files[0].filePath,
      });
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const isLoading =
    isUploading || completeVerificationMutation.isLoading;
  const { trigger } = form;

  return (
    <div className="container mx-auto py-4 px-2 sm:py-12 sm:px-4 animate-fade-in">
      <div className="max-w-3xl mx-auto space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold">Account Verification</h1>
          <p className="text-muted-foreground">
            To ensure platform safety, please complete these steps.
          </p>
        </div>
        <StepIndicator currentStep={step} />
        <Card className="glass">
          <CardContent className="p-8">
            <TooltipProvider>
              <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-8"
              >
                <AnimatePresence mode="wait">
                  {step === 1 && (
                    <motion.div
                      key="step1"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      className="space-y-6"
                    >
                      <CardTitle className="flex items-center gap-2">
                        <User /> Personal Details
                      </CardTitle>
                      <FormField
                        control={form.control}
                        name="gender"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="flex items-center gap-2">
                              Gender *
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>This information helps us verify your identity</p>
                                </TooltipContent>
                              </Tooltip>
                            </FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select gender..." />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="MALE">Male</SelectItem>
                                <SelectItem value="FEMALE">Female</SelectItem>
                                <SelectItem value="OTHER">Other</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="currentResidence"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="flex items-center gap-2">
                              Current Residence *
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Provide your current residential address</p>
                                  <p className="text-xs text-muted-foreground">e.g., Area 47, Sector 3, Lilongwe</p>
                                </TooltipContent>
                              </Tooltip>
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder="e.g., Area 47, Lilongwe"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <div className="flex justify-end">
                        <Button
                          type="button"
                          onClick={async () => {
                            const ok = await trigger([
                              "gender",
                              "currentResidence",
                            ]);
                            if (ok) setStep(2);
                          }}
                        >
                          Next <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </div>
                    </motion.div>
                  )}

                  {step === 2 && (
                    <motion.div
                      key="step2"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      className="space-y-6"
                    >
                      <CardTitle className="flex items-center gap-2">
                        <FileImage /> National ID
                      </CardTitle>
                      <Alert>
                        <AlertDescription>
                          Provide a clear, front-side image of your Malawian National ID.
                          Choose camera for auto-capture or upload an existing photo.
                        </AlertDescription>
                      </Alert>
                      
                      <Tabs value={idCaptureMethod} onValueChange={(v) => setIdCaptureMethod(v as 'camera' | 'upload')}>
                        <TabsList className="grid w-full grid-cols-2">
                          <TabsTrigger value="camera" className="flex items-center gap-2">
                            <Camera className="h-4 w-4" /> Camera
                          </TabsTrigger>
                          <TabsTrigger value="upload" className="flex items-center gap-2">
                            <Upload className="h-4 w-4" /> Upload
                          </TabsTrigger>
                        </TabsList>
                        
                        <TabsContent value="camera" className="mt-4">
                          <FormField
                            control={form.control}
                            name="nationalIdFile"
                            render={({ field }) => (
                              <FormItem className="space-y-4">
                                <AutoCaptureIDWebcam
                                  onCapture={(file) => {
                                    setCapturedIdImage(URL.createObjectURL(file));
                                    field.onChange([file]);
                                  }}
                                  onRecapture={onIdRecapture}
                                  capturedImage={capturedIdImage}
                                />
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </TabsContent>
                        
                        <TabsContent value="upload" className="mt-4">
                          <FormField
                            control={form.control}
                            name="nationalIdFile"
                            render={() => (
                              <FormItem className="space-y-4">
                                <div className="flex flex-col items-center gap-4 p-8 border-2 border-dashed rounded-lg">
                                  {capturedIdImage ? (
                                    <>
                                      <img src={capturedIdImage} alt="National ID" className="max-w-full h-auto rounded-lg" />
                                      <Button type="button" variant="outline" onClick={onIdRecapture}>
                                        Choose Different Image
                                      </Button>
                                    </>
                                  ) : (
                                    <>
                                      <FileImage className="h-12 w-12 text-muted-foreground" />
                                      <div className="text-center">
                                        <p className="text-sm font-medium">Upload National ID</p>
                                        <p className="text-xs text-muted-foreground">JPG, PNG or WEBP (max 5MB)</p>
                                      </div>
                                      <Input
                                        type="file"
                                        accept="image/jpeg,image/jpg,image/png,image/webp"
                                        onChange={onIdFileUpload}
                                        className="max-w-xs"
                                      />
                                    </>
                                  )}
                                </div>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </TabsContent>
                      </Tabs>
                      
                      <div className="flex justify-between">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setStep(1)}
                        >
                          Back
                        </Button>
                        <Button
                          type="button"
                          onClick={async () => {
                            const ok = await trigger("nationalIdFile");
                            if (ok) setStep(3);
                          }}
                        >
                          Next <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </div>
                    </motion.div>
                  )}

                  {step === 3 && (
                    <motion.div
                      key="step3"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      className="space-y-6"
                    >
                      <CardTitle className="flex items-center gap-2">
                        <UserCheck /> Live Photo
                      </CardTitle>
                      <Alert>
                        <AlertDescription>
                          For security purposes, you must take a live photo using your camera.
                          Center your face and we'll auto-capture when ready.
                        </AlertDescription>
                      </Alert>
                      
                      <FaceMeshWebcam
                        onCapture={onFaceCapture}
                        onRecapture={onFaceRecapture}
                        capturedImage={capturedFaceImage}
                      />
                      <FormField
                        control={form.control}
                        name="profilePictureFile"
                        render={() => <FormMessage />}
                      />
                      
                      <div className="flex justify-between">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setStep(2)}
                        >
                          Back
                        </Button>
                        <Button
                          type="submit"
                          variant="premium"
                          disabled={isLoading || !capturedFaceImage}
                        >
                          {isLoading && (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          )}
                          Submit for Verification
                        </Button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </form>
            </Form>
          </TooltipProvider>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VerificationPage;
